import React, { useMemo } from 'react';
import moment from 'moment';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { EventContentArg } from '@fullcalendar/core';
import classNames from 'classnames';
import { EventType } from './hooks/useGetEventClassNames';
import { ModeratedTaskSetting } from '../../queries';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    height: '100%',
  },
  time: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    ...theme.fonts.calendarText,
    paddingLeft: theme.marginBase,
    color: theme.colors.greenAccessible,
  },
  freeBusy: {},
  free: {},
  busy: {
    background: `repeating-linear-gradient(45deg, ${theme.colors.grayBackground} 0px, ${theme.colors.grayBackground} 5px, ${theme.colors.redMuted} 5px, ${theme.colors.redMuted} 10px)`,
  },
}));

interface Props {
  eventInfo: EventContentArg;
  settings?: ModeratedTaskSetting;
}

interface Event {
  start?: Date;
  end?: Date;
}
export const mergeEvents = (events: Event[], buffer: number): Event[] => {
  events.sort((a, b) => {
    return (a.start?.getTime() || 0) - (b.start?.getTime() || 0);
  });

  const mergedEvents: Event[] = [];
  let currentEvent: Event | undefined = undefined;
  for (const event of events) {
    const eventStart = event.start
      ? moment(event.start).subtract(buffer, 'minutes').toDate()
      : undefined;
    const eventEnd = event.end
      ? moment(event.end).add(buffer, 'minutes').toDate()
      : undefined;

    if (!currentEvent) {
      currentEvent = { start: eventStart, end: eventEnd };
      continue;
    }
    if (!currentEvent.end) {
      mergedEvents.push(currentEvent);
      return mergedEvents;
    }
    if (!eventStart) {
      if (!eventEnd) {
        return [{}];
      }

      if (eventEnd > currentEvent.end) {
        currentEvent.end = eventEnd;
      }
      continue;
    }

    if (eventStart <= currentEvent.end) {
      if (!eventEnd || eventEnd > currentEvent.end) {
        currentEvent.end = eventEnd;
      }
    } else {
      mergedEvents.push(currentEvent);
      currentEvent = { start: eventStart, end: eventEnd };
    }
  }
  if (currentEvent) {
    mergedEvents.push(currentEvent);
  }
  return mergedEvents;
};

const getEventInfoStartEnd = (eventInfo: EventContentArg) => {
  let start = eventInfo.event.start;
  let end = eventInfo.event.end;
  if (eventInfo.isStart && !eventInfo.isEnd) {
    end = moment(start).endOf('day').toDate();
  }
  if (!eventInfo.isStart && eventInfo.isEnd) {
    start = moment(end).startOf('day').toDate();
  }
  if (!eventInfo.isStart && !eventInfo.isEnd) {
    return {};
  }
  return { start, end };
};

export const CalendarEventContent = ({ eventInfo, settings }: Props) => {
  const classes = useStyles();

  const freeBusy = useMemo(() => {
    const events = eventInfo.view.calendar.getEvents();

    const { start, end } = getEventInfoStartEnd(eventInfo);
    if (!start || !end) {
      return [];
    }

    const buffer = (settings?.appointmentBuffer || 0) * 60 * 1000;
    const conflicting = events.filter((event) => {
      if (!event.extendedProps.type) {
        return false;
      }
      if (event.extendedProps.type === EventType.AVAILABILITY) {
        return false;
      }
      if (
        !settings?.shouldCheckForConflicts &&
        event.extendedProps.type === EventType.EVENT &&
        !event.extendedProps.fromTandemz
      ) {
        return false;
      }
      if (
        settings?.ignoreAvailableEvents &&
        event.extendedProps.freeBusyStatus === 'free'
      ) {
        return false;
      }
      const eventStart = (event.start?.getTime() || 0) - buffer;
      const eventEnd = event.end ? event.end.getTime() + buffer : undefined;

      if (eventEnd && start && eventEnd <= start.getTime()) {
        return false;
      }
      if (end && eventStart >= end.getTime()) {
        return false;
      }
      return true;
    }) as Array<{ start?: Date; end?: Date }>;

    const globalStart = settings
      ? moment()
          .add(settings.bookingDelayInHours, 'hours')
          .subtract(buffer, 'millisecond')
          .toDate()
      : moment().subtract(buffer, 'millisecond').toDate();
    if (globalStart > start) {
      conflicting.push({
        end: globalStart,
      });
    }

    const mergedEvents = mergeEvents(
      conflicting,
      settings?.appointmentBuffer || 0,
    );

    let lastEnd: Date | null = null;
    const freeBusy = [] as Array<{ relativeSize: number; busy: boolean }>;

    const createFreeBusy = (
      busy: boolean,
      eventStart?: Date,
      eventEnd?: Date,
    ) => {
      const startInBoundaries = eventStart
        ? Math.max(start.getTime(), eventStart.getTime())
        : start.getTime();

      const endInBoundaries = eventEnd
        ? Math.min(end.getTime(), eventEnd.getTime())
        : end.getTime();

      const relativeSize = Math.round(
        (endInBoundaries - startInBoundaries) / (1000 * 60),
      );
      return { relativeSize, busy };
    };

    for (const event of mergedEvents) {
      if (!lastEnd) {
        if (event.start && event.start.getTime() > start.getTime()) {
          freeBusy.push(createFreeBusy(false, start, event.start));
        }
      } else {
        freeBusy.push(createFreeBusy(false, lastEnd, event.start));
      }
      freeBusy.push(createFreeBusy(true, event.start, event.end));
      if (event.end) {
        lastEnd = event.end;
      } else {
        return freeBusy;
      }
    }
    if (lastEnd && lastEnd.getTime() < end.getTime()) {
      freeBusy.push(createFreeBusy(false, lastEnd, end));
    }
    return freeBusy;
  }, [eventInfo, settings]);

  return (
    <div className={classes.container}>
      <b className={classes.time}>{eventInfo.timeText}</b>
      {freeBusy.map((item, index) => (
        <div
          key={index}
          style={{
            flex: item.relativeSize,
          }}
          className={classNames(classes.freeBusy, {
            [classes.free]: !item.busy,
            [classes.busy]: item.busy,
          })}
        />
      ))}
    </div>
  );
};
